import { Component } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { Tilt } from "./tilt";
import { LotusBloom } from "./lotus-bloom";
import { IslandMap } from "./island-map";
import type { RouteStop } from "./data";

const DOSHAS = [
  {
    icon: "wind",
    name: "Vata",
    element: "Air & Space",
    copy: "Governs movement, breath and circulation. In balance it brings creativity and energy; out of balance, restlessness and dry, cold discomfort.",
  },
  {
    icon: "flame",
    name: "Pitta",
    element: "Fire & Water",
    copy: "Governs digestion, metabolism and transformation. In balance it brings focus and warmth; out of balance, irritability and inflammation.",
  },
  {
    icon: "mountain",
    name: "Kapha",
    element: "Earth & Water",
    copy: "Governs structure, immunity and stability. In balance it brings calm and strength; out of balance, heaviness and stagnation.",
  },
];

const OFFERINGS = [
  {
    icon: "leaf",
    title: "Ayurveda & Indigenous Medicine",
    copy: "Traditional herbal treatments, oil therapies and detox programmes rooted in local heritage and Hela Wedakama, Sri Lanka's own indigenous medicine.",
  },
  {
    icon: "moon",
    title: "Mindfulness & Spirituality",
    copy: "Buddhist temple stays, guided meditation and silent yoga retreats, built around a 2,500-year-old contemplative tradition.",
  },
  {
    icon: "mountain",
    title: "Nature & Forest Therapy",
    copy: "Eco-friendly wellness walks through misty tea plantations in the hills, or tropical lowland reserves along the coast.",
  },
];

const PANCHAKARMA = [
  {
    n: "01",
    name: "Vamana",
    copy: "A supervised, therapeutic emesis that clears toxins from the upper stomach and respiratory tract.",
  },
  {
    n: "02",
    name: "Virechana",
    copy: "Purgation therapy using herbal laxatives to cleanse the small intestine and support the liver.",
  },
  {
    n: "03",
    name: "Basti",
    copy: "Herbal oil or decoction enemas, used to clear accumulated toxins from the colon.",
  },
  {
    n: "04",
    name: "Nasya",
    copy: "Medicinal oils or powders administered through the nose to clear the head and sinus passages.",
  },
  {
    n: "05",
    name: "Raktamokshana",
    copy: "Controlled bloodletting, used in specialised cases to purify the blood.",
  },
];

const EXTERNAL_THERAPIES = [
  {
    icon: "droplet",
    name: "Abhyanga",
    copy: "A full-body warm herbal-oil massage that calms the nervous system and improves circulation - the treatment most first-time visitors try.",
  },
  {
    icon: "waves",
    name: "Shirodhara",
    copy: "A steady, unbroken stream of warm oil poured across the forehead, held there until the mind quiets.",
  },
  {
    icon: "flame",
    name: "Pinda Swedana",
    copy: "A sweating therapy using warm boluses of cooked medicated rice, herbs and powders, applied rhythmically across the body.",
  },
];

interface Retreat {
  name: string;
  location: string;
  region: string;
  style: string;
  copy: string;
  detail: string;
  image: string;
  href: string;
}

const RETREATS: Retreat[] = [
  {
    name: "Santani Wellness Resort & Spa",
    location: "Kandy, Hill Country",
    region: "Hill Country",
    style: "Architectural retreat",
    copy: "Set into a former tea estate above Kandy, Santani pairs an award-winning, architect-led design with structured wellness programmes built around sleep, detox and stress recovery rather than a single spa menu.",
    detail: "Individual glass-walled cabins spread across a forested ridge, each built to disappear into the hillside.",
    image: "./ayurveda-herbs-1.jpg",
    href: "https://www.santani.com/santaniwellness-kandy/",
  },
  {
    name: "Jetwing Ayurveda Pavilions",
    location: "Negombo, West Coast",
    region: "West Coast",
    style: "Authentic Ayurveda resort",
    copy: "A dedicated Ayurveda property from the Jetwing group, built around resident physicians, personalised dosha consultations and a full traditional treatment programme just minutes from Negombo's beach and the international airport.",
    detail: "Every stay opens with a physician consultation before any treatment plan is written.",
    image: "./ayurveda-oil-1.jpg",
    href: "https://www.jetwinghotels.com/jetwingayurvedapavilions/",
  },
  {
    name: "Barberyn Ayurveda Resorts",
    location: "Beruwala & Weligama, South & West Coast",
    region: "South & West Coast",
    style: "Medical Ayurveda pioneer",
    copy: "Founded in 1984 as Sri Lanka's very first dedicated Ayurveda resort, Barberyn now runs oceanfront properties in both Beruwala and Weligama, with a medically supervised approach that leans closer to clinical treatment than spa relaxation.",
    detail: "Four decades of continuous operation make it one of the most established Ayurveda addresses on the island.",
    image: "./ayurveda-cinnamon-1.jpg",
    href: "https://www.barberynresorts.com",
  },
  {
    name: "Siddhalepa Ayurveda Resort",
    location: "Wadduwa, West Coast",
    region: "West Coast",
    style: "Heritage brand resort",
    copy: "Run by the Hettigoda family behind Siddhalepa, Sri Lanka's best-known Ayurvedic balm since 1934, this beachfront Wadduwa resort pairs nine decades of herbal manufacturing heritage with structured treatment programmes for both short stays and longer cures.",
    detail: "Backed by the same herbal formulary that has supplied Sri Lankan households for generations.",
    image: "./ayurveda-lotus-1.jpg",
    href: "https://www.siddhalepahotels.com/ayurveda-resort-wadduwa/ayurveda-treatments/",
  },
];

const STATS = [
  { value: "5,000+", label: "Years of Tradition" },
  { value: "3", label: "Doshas" },
  { value: "5", label: "Panchakarma Therapies" },
  { value: "3", label: "Wellness Regions" },
];

const REGION_STOPS: (RouteStop & { blurb: string })[] = [
  {
    code: "HIL",
    name: "Hill Country",
    x: 216,
    y: 531,
    blurb:
      "Kandy, Bandarawela and Ella: cooler mountain air, quiet tea estates, and high-altitude retreats such as Santani and Anasa Wellness Resort.",
  },
  {
    code: "CST",
    name: "South & West Coast",
    x: 95,
    y: 715,
    blurb:
      "Tangalle, Beruwala and Weligama: oceanfront yoga pavilions and specialised medical spas such as Barberyn Ayurveda Resorts and Sen Wellness Sanctuary.",
  },
  {
    code: "CUL",
    name: "Cultural Triangle",
    x: 232,
    y: 372,
    blurb:
      "Sigiriya and its surrounds: quiet historical spaces that pair ancient heritage with mindful reflection.",
  },
];

@Component({
  selector: "app-ayurveda",
  imports: [RouterLink, Icon, Reveal, Tilt, LotusBloom, IslandMap],
  template: `
    <section
      class="grain relative pt-40 pb-32 surface-ink overflow-hidden"
    >
      <div
        class="mesh-blob mesh-blob-a w-[480px] h-[480px] -top-40 -right-24 bg-teal/20"
      ></div>
      <div
        class="mesh-blob mesh-blob-b w-[320px] h-[320px] bottom-0 left-0 bg-saffron/10"
      ></div>
      <div
        class="container relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center"
      >
        <div class="lg:col-span-7">
          <div class="flex items-center gap-2 mb-6" appReveal="fade">
            <div class="w-8 h-[2px] bg-saffron"></div>
            <span
              class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
              >Wellness & Healing</span
            >
          </div>
          <h1
            class="text-6xl md:text-7xl font-serif leading-[0.95] tracking-tight"
            appReveal
            [revealDelay]="0.1"
          >
            Sri Lanka's other
            <span class="italic font-light text-paper/50">ancient science.</span>
          </h1>
          <p
            class="max-w-xl text-paper/60 leading-relaxed text-lg mt-6"
            appReveal
            [revealDelay]="0.2"
          >
            Alongside its temples and tea, this island holds one of the
            world's oldest continuous wellness traditions - a growing global
            destination that pairs 5,000-year-old Ayurvedic medicine with
            Buddhist mindfulness and nature-focused healing, from hill-country
            estates to oceanfront treatment pavilions.
          </p>
          <div
            class="flex flex-col sm:flex-row gap-4 mt-10"
            appReveal
            [revealDelay]="0.3"
          >
            <a
              routerLink="/contact"
              [queryParams]="{ topic: 'Ayurveda & Wellness Retreat' }"
              class="btn-saffron justify-center"
            >
              Plan a Wellness Retreat
            </a>
            <a href="#retreats" class="btn-outline-light justify-center">
              See Featured Retreats
            </a>
          </div>
        </div>
        <div
          class="lg:col-span-5 flex justify-center lg:justify-end"
          appReveal="scale"
          [revealDelay]="0.15"
        >
          <app-lotus-bloom />
        </div>
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-teal text-xs">WHAT IS AYURVEDA</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            Knowledge of life,
            <span class="italic font-light text-ink/40">in three forces.</span>
          </h2>
          <p class="text-ink/60 leading-relaxed text-lg mt-6">
            The word comes from Sanskrit - <em>Ayur</em>, life, and
            <em>Veda</em>, knowledge. Ayurveda is a traditional system of
            medicine, thousands of years old, built on keeping the body and
            mind in balance through diet, daily routine, movement, massage,
            meditation and herbal preparation. At its centre are three
            doshas - Vata, Pitta and Kapha - believed to shape a person's
            physical and mental character.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
          @for (d of doshas; track d.name; let i = $index) {
            <div
              appTilt
              class="bg-paper rounded-[1.5rem] p-8 border border-sand/60 hover:border-teal/40 hover:shadow-2xl transition-all"
              appReveal
              [revealDelay]="i * 0.08"
            >
              <div
                class="w-12 h-12 flex items-center justify-center bg-ink text-saffron rounded-2xl mb-6"
              >
                <app-icon [name]="d.icon" [size]="20" />
              </div>
              <h3 class="font-serif text-2xl mb-1">{{ d.name }}</h3>
              <div
                class="text-teal text-[10px] uppercase tracking-widest font-bold mb-4"
              >
                {{ d.element }}
              </div>
              <p class="text-ink/55 text-sm leading-relaxed">{{ d.copy }}</p>
            </div>
          }
        </div>
        <p class="text-ink/40 text-xs leading-relaxed mt-8 max-w-2xl">
          Practices such as yoga, meditation and mindful eating may support
          general wellbeing, but Ayurvedic medicine is not a scientifically
          established substitute for modern medical treatment, and some
          herbal preparations can interact with medication. Always mention
          any existing condition to your practitioner before a treatment
          programme begins.
        </p>
      </div>
    </section>

    <section class="grain relative surface-ink py-28 overflow-hidden">
      <div
        class="mesh-blob mesh-blob-a w-[400px] h-[400px] top-0 left-1/4 bg-teal/15"
      ></div>
      <div class="container relative z-10">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-saffron text-xs">WELLNESS TOURISM</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            A destination built on
            <span class="italic font-light text-paper/50">three offerings.</span>
          </h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
          @for (o of offerings; track o.title; let i = $index) {
            <div
              class="glass rounded-[1.5rem] p-8"
              appReveal
              [revealDelay]="i * 0.08"
            >
              <div
                class="w-12 h-12 flex items-center justify-center bg-saffron text-ink rounded-2xl mb-6"
              >
                <app-icon [name]="o.icon" [size]="20" />
              </div>
              <h3 class="font-serif text-xl mb-3">{{ o.title }}</h3>
              <p class="text-ink/60 text-sm leading-relaxed">{{ o.copy }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section class="py-28 bg-paper-soft overflow-hidden">
      <div class="container">
        <div
          class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        >
          <div appReveal>
            <span class="route-code text-teal text-xs">POPULAR REGIONS</span>
            <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4 mb-8">
              Three regions,
              <span class="italic font-light text-ink/40"
                >three kinds of retreat.</span
              >
            </h2>
            <div class="space-y-6">
              @for (r of regionStops; track r.code; let i = $index) {
                <div
                  class="flex gap-4 pb-6 border-b border-sand/60 last:border-0 last:pb-0"
                  appReveal
                  [revealDelay]="i * 0.08"
                >
                  <div class="route-code text-sand text-2xl shrink-0 w-12">
                    {{ r.code }}
                  </div>
                  <div>
                    <h3 class="font-serif text-lg mb-1">{{ r.name }}</h3>
                    <p class="text-ink/55 text-sm leading-relaxed">
                      {{ r.blurb }}
                    </p>
                  </div>
                </div>
              }
            </div>
          </div>
          <div appReveal="scale" [revealDelay]="0.1">
            <app-island-map [stops]="regionStops" />
          </div>
        </div>
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-teal text-xs">TREATMENTS</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            Panchakarma -
            <span class="italic font-light text-ink/40"
              >the five cleansing therapies.</span
            >
          </h2>
          <p class="text-ink/60 leading-relaxed text-lg mt-6">
            Panchakarma is the core group of five procedures Ayurvedic
            medicine uses to clear accumulated toxins from the body - the
            deepest tier of a treatment programme, typically prescribed and
            supervised by a resident physician.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-5 gap-8 mb-20">
          @for (p of panchakarma; track p.n; let i = $index) {
            <div appReveal [revealDelay]="i * 0.08">
              <div class="route-code text-4xl text-sand mb-4">{{ p.n }}</div>
              <h3 class="font-serif text-lg mb-2">{{ p.name }}</h3>
              <p class="text-ink/55 text-sm leading-relaxed">{{ p.copy }}</p>
            </div>
          }
        </div>

        <div class="max-w-2xl mb-12" appReveal="fade">
          <span class="route-code text-teal text-xs">EXTERNAL THERAPIES</span>
          <h2 class="text-3xl md:text-4xl font-serif leading-tight mt-4">
            The treatments most visitors actually try
          </h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
          @for (t of externalTherapies; track t.name; let i = $index) {
            <div
              appTilt
              class="bg-paper rounded-[1.5rem] p-8 border border-sand/60 hover:border-vermilion/40 hover:shadow-2xl transition-all"
              appReveal
              [revealDelay]="i * 0.08"
            >
              <div
                class="w-12 h-12 flex items-center justify-center bg-ink text-saffron rounded-2xl mb-6"
              >
                <app-icon [name]="t.icon" [size]="20" />
              </div>
              <h3 class="font-serif text-xl mb-3">{{ t.name }}</h3>
              <p class="text-ink/55 text-sm leading-relaxed">{{ t.copy }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section id="retreats" class="grain relative surface-ink py-28 overflow-hidden scroll-mt-24">
      <div
        class="mesh-blob mesh-blob-b w-[350px] h-[350px] bottom-0 -right-24 bg-teal/15"
      ></div>
      <div class="container relative z-10">
        <div class="max-w-2xl mb-16" appReveal="fade">
          <span class="route-code text-saffron text-xs">FEATURED RETREATS</span>
          <h2 class="text-4xl md:text-5xl font-serif leading-tight mt-4">
            Four addresses,
            <span class="italic font-light text-paper/50"
              >four different cures.</span
            >
          </h2>
          <p class="text-paper/60 leading-relaxed text-lg mt-6">
            We don't operate these resorts - we route travelers to them.
            Each takes a genuinely different approach to Ayurveda, from
            architect-led relaxation to medically supervised treatment
            programmes.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-8">
          @for (r of retreats; track r.name; let i = $index) {
            <a
              [href]="r.href"
              target="_blank"
              rel="noopener noreferrer"
              appTilt
              [appTiltMax]="3"
              class="group relative overflow-hidden rounded-[2rem] block"
              appReveal
              [revealDelay]="i * 0.08"
            >
              <div class="relative h-56 overflow-hidden">
                <img
                  [src]="r.image"
                  [alt]="r.name"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  referrerpolicy="no-referrer"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent"
                ></div>
                <div
                  class="absolute top-4 left-4 route-code text-[10px] glass-ink text-paper px-2.5 py-1 rounded-full"
                >
                  {{ r.region }}
                </div>
              </div>
              <div class="bg-paper p-7">
                <div
                  class="text-teal text-[10px] uppercase tracking-widest font-bold mb-2"
                >
                  {{ r.style }}
                </div>
                <h3 class="font-serif text-2xl mb-1 text-ink group-hover:text-vermilion transition-colors">
                  {{ r.name }}
                </h3>
                <div class="flex items-center gap-1.5 text-ink/45 text-xs mb-4">
                  <app-icon name="pin" [size]="12" />
                  {{ r.location }}
                </div>
                <p class="text-ink/60 text-sm leading-relaxed mb-4">
                  {{ r.copy }}
                </p>
                <p
                  class="text-ink/40 text-xs italic leading-relaxed border-t border-sand/60 pt-4"
                >
                  {{ r.detail }}
                </p>
              </div>
            </a>
          }
        </div>
        <p class="text-paper/35 text-xs leading-relaxed mt-10 max-w-2xl">
          Each resort above is independently owned and operated - we can help
          you plan a stay and combine it with a wider route across the
          island, but treatment programmes, pricing and availability are set
          by the resorts themselves.
        </p>
      </div>
    </section>

    <section class="grain relative surface-ink py-24 overflow-hidden">
      <div
        class="container relative z-10 grid grid-cols-2 md:grid-cols-4 gap-8"
      >
        @for (stat of stats; track stat.label; let i = $index) {
          <div class="text-center" appReveal="scale" [revealDelay]="i * 0.08">
            <div class="route-code text-4xl sm:text-5xl text-saffron mb-2">
              {{ stat.value }}
            </div>
            <div
              class="text-paper/50 text-[11px] uppercase tracking-widest font-bold"
            >
              {{ stat.label }}
            </div>
          </div>
        }
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div
          class="relative overflow-hidden bg-paper-soft rounded-[2.5rem] p-14 sm:p-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10"
          appReveal="scale"
        >
          <div class="max-w-xl space-y-5">
            <h2 class="text-4xl sm:text-5xl font-serif leading-tight">
              Deep cure, or
              <span class="italic font-light text-ink/40"
                >gentle relaxation?</span
              >
            </h2>
            <p class="text-ink/60 leading-relaxed">
              Tell us which you're after, and whether you'd rather be in the
              cool mountains or on the warm coast - we'll route the rest.
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 shrink-0">
            <a
              routerLink="/contact"
              [queryParams]="{ topic: 'Ayurveda & Wellness Retreat' }"
              class="btn-vermilion justify-center"
              >Plan a Wellness Retreat</a
            >
            <a routerLink="/tours" class="btn-outline justify-center"
              >Browse Tours</a
            >
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Ayurveda {
  doshas = DOSHAS;
  offerings = OFFERINGS;
  panchakarma = PANCHAKARMA;
  externalTherapies = EXTERNAL_THERAPIES;
  retreats = RETREATS;
  stats = STATS;
  regionStops = REGION_STOPS;
}
